<template>
    <div
        class="uik-datepicker"
        :class="{'uik-datepicker--error': errorMessage}"
    >
        <div v-if="label" class="uik-datepicker__label">{{ getLabel }}</div>

        <DatePicker
            v-bind="props"
            class="uik-datepicker__datepicker"
            @input="$emit('input', $event)"
            @change="$emit('change', $event)"
            @open="$emit('open', $event)"
            @close="$emit('close', $event)"
            @confirm="$emit('confirm', $event)"
            @clear="$emit('clear', $event)"
            @input-error="$emit('input-error', $event)"
            @focus="$emit('focus', $event)"
            @blur="$emit('blur', $event)"
            @pick="$emit('pick', $event)"
            @calendar-change="$emit('calendar-change', $event)"
            @panel-change="$emit('panel-change', $event)"
        >
            <template v-if="$slots['icon-calendar']" v-slot:icon-calendar><slot name="icon-calendar"/></template>
            <template v-if="$slots['icon-clear']" v-slot:icon-clear><slot name="icon-clear"/></template>
            <template v-if="$slots.input" v-slot:input><slot name="input"/></template>
            <template v-if="$slots.header" v-slot:header><slot name="header"/></template>
            <template v-if="$slots.footer" v-slot:footer><slot name="footer"/></template>
            <template v-if="$slots.sidebar" v-slot:sidebar><slot name="sidebar"/></template>
        </DatePicker>

        <p v-if="errorMessage" class="uik-datepicker__error-message">{{ errorMessage }}</p>
    </div>
</template>

<script>
import DatePicker from "vue2-datepicker3"
import { locale } from "./Locale"

export default {
  name: "UikDatepicker",
  components: { DatePicker },
  model: {
    prop: "value"
  },
  props: {
    /**
     * Append the popup to body
     */
    appendToBody: { type: Boolean, default: true },
    /**
     * N/S
     */
    calendar: {},
    /**
     * If false, don't show the clear icon
     */
    clearable: { type: Boolean, default: false },
    /**
     * If true, need click the button to change value
     */
    confirm: { type: Boolean, default: false },
    /**
     * The text of confirm button
     */
    confirmText: { type: String },
    /**
     * Default panel of the picker
     * year|month
     */
    defaultPanel: {},
    /**
     * Default date of the calendar
     */
    defaultValue: { type: Date, default () { return new Date() } },
    /**
     * Disable the component
     */
    disabled: { type: Boolean, default: false },
    /**
     * Specify the date that cannot be selected
     * (date: Date, currentValue: Date[]) => boolean
     */
    disabledDate: {},
    /**
     * Specify the time that cannot be selected
     * (date: Date) => boolean
     */
    disabledTime: {},
    /**
     * Whether the input is editable
     */
    editable: { type: Boolean, default: true },
    /**
     * To set the date format. similar to moment.js
     */
    format: { type: String, default: undefined },
    /**
     * Use your own formatter, such as moment.js
     */
    formatter: { type: Object },
    /**
     * N/S
     */
    getClasses: {},
    /**
     * N/S
     */
    getYearPanel: {},
    /**
     * Custom hour column
     * Number[]
     */
    hourOptions: { type: Array },
    /**
     * Interval between hours in time picker
     * 1-60
     */
    hourStep: { type: Number, default: 1 },
    /**
     * Without input
     */
    inline: { type: Boolean, default: false },
    /**
     * Input attrs(eg: { name: 'date', id: 'foo'})
     */
    inputAttr: { type: Object, default () { return {} } },
    /**
     * Input classname
     */
    inputClass: { type: String, default: "mx-input" },
    /**
     * Custom minute column
     * Number[]
     */
    minuteOptions: { type: Array },
    /**
     * Interval between minutes in time picker
     * 1-60
     */
    minuteStep: { type: Number, default: 1 },
    /**
     * If true, multi-select date
     */
    multiple: { type: Boolean, default: false },
    /**
     * Open state of picker
     */
    open: { type: Boolean, default: undefined },
    /**
     * Whether update date when select year or month
     */
    partialUpdate: { type: Boolean, default: false },
    /**
     * Input placeholder text
     */
    placeholder: { type: String, default: "" },
    /**
     * Popup classes
     */
    popupClass: { type: String },
    /**
     * Popup style
     */
    popupStyle: { type: Object, default () { return {} } },
    /**
     * Set prefix class
     */
    prefixClass: { type: String, default: "mx" },
    /**
     * If true, pick the range date
     */
    range: { type: Boolean, default: false },
    /**
     * Text of range separator
     */
    rangeSeparator: { type: String, default: "-" },
    /**
     * N/S
     */
    renderInputText: {},
    /**
     * Set the duration of scroll when hour is selected
     */
    scrollDuration: { type: Number, default: 100 },
    /**
     * Custom second column
     * Number[]
     */
    secondOptions: { type: Array },
    /**
     * Interval between seconds in time picker
     * 1-60
     */
    secondStep: { type: Number, default: 1 },
    /**
     * Set shortcuts to select
     * {text, onClick}[]
     */
    shortcuts: { type: Array, default: null },
    /**
     * Get premade shortcuts (currently supported for type 'date')
     */
    withShortcuts: { type: Boolean, default: false },
    /**
     * Whether show hour column
     */
    showHour: { type: Boolean, default: undefined },
    /**
     * Whether show minute column
     */
    showMinute: { type: Boolean, default: undefined },
    /**
     * Whether show second column
     */
    showSecond: { type: Boolean, default: false },
    /**
     * Whether show header of time picker
     */
    showTimeHeader: { type: Boolean, default: false },
    /**
     * N/S
     */
    showTimePanel: { type: Boolean, default: undefined },
    /**
     * Determine whether show week number
     */
    showWeekNumber: { type: Boolean, default: undefined },
    /**
     * Set fixed time list to select
     * {start: '00:00', step:'00:30' , end: '23:30', format: 'HH:mm' }
     * start - start time
     * step - step time
     * end - end time
     * format - the default is same as prop format
     */
    timePickerOptions: { type: Object, default: null },
    /**
     * Format of the time header
     */
    timeTitleFormat: { type: String },
    /**
     * Format of the tooltip in calendar cell
     */
    titleFormat: { type: String },
    /**
     * Select the type of picker
     */
    type: {
      type: String,
      default: "date",
      validator (value) {
        return [
          "date",
          "datetime",
          "year",
          "month",
          "time",
          "week"
        ].indexOf(value) !== -1
      }
    },
    /**
     * Whether show AM/PM column
     */
    use12h: { type: Boolean, default: undefined },
    /**
     * Datepicker value
     */
    value: { default: "" },
    /**
     * Data type of the binding value
     * 'date' - return a Date object
     * 'timestamp' - return a timestamp number
     * 'format' - returns a string formatted using pattern of format
     * token(MM/DD/YYYY) - returns a string formatted using this pattern
     */
    valueType: { type: String },
    /**
     * Choose between premade languages
     */
    lang: {
      type: String,
      default: "",
      validator (value) {
        return ["", "en", "sl"].indexOf(value) !== -1
      }
    },
    /**
     * Display error message
     */
    errorMessage: { type: String, default: "" },
    /**
     * Text above input
     */
    label: { type: String, default: "" },
    /**
     * Is value required
     */
    required: { type: Boolean, default: false }
  },
  data () {
    return {
      config: {}
    }
  },
  computed: {
    getLabel () {
      if (!this.label) return ""

      let label = this.label
      if (this.required) label += " *"

      return label
    },
    settings () {
      const lang = this.lang || this.config.lang || "en"
      const format = this.format || this.config.format || locale[lang].format[this.type]
      const valueType = this.valueType || this.config.valueType || "YYYY-MM-DD HH:mm:ss"
      const timeTitleFormat = this.timeTitleFormat || this.config.timeTitleFormat || locale[lang].format.time
      const titleFormat = this.titleFormat || this.config.titleFormat || locale[lang].format.date
      const use12h = this.use12h || this.config.use12h || locale[lang].format.time?.slice(0, 2) === "hh"

      return {
        ...this.config,
        lang,
        format,
        valueType,
        timeTitleFormat,
        titleFormat,
        use12h
      }
    },
    locale () {
      let lang = locale[this.settings.lang]

      const localeProps = ["confirmText", "placeholder"]

      for (const i in localeProps) {
        const prop = localeProps[i]
        const value = this[prop]
        if (value) lang[prop] = value
      }

      return lang
    },
    getShortcuts () {
      if (!this.withShortcuts || this.type !== "date") return []

      if (this.range) {
        const lang = locale[this.settings.lang].shortcuts.range
        return [
          {
            text: lang.today,
            onClick: () => { return [this.moment().startOf("day")._d, this.moment().endOf("day")._d] }
          },
          {
            text: lang.this_week,
            onClick: () => { return [this.moment().startOf("week")._d, this.moment().endOf("week")._d] }
          },
          {
            text: lang.this_month,
            onClick: () => { return [this.moment().startOf("month")._d, this.moment().endOf("month")._d] }
          },
          {
            text: lang.this_year,
            onClick: () => { return [this.moment().startOf("year")._d, this.moment().endOf("year")._d] }
          },
          {
            text: lang.last_week,
            onClick: () => { return [this.moment().subtract(1, "week").startOf("week")._d, this.moment().subtract(1, "week").endOf("week")._d] }
          },
          {
            text: lang.last_month,
            onClick: () => { return [this.moment().subtract(1, "month").startOf("month")._d, this.moment().subtract(1, "month").endOf("month")._d] }
          },
          {
            text: lang.next_week,
            onClick: () => { return [this.moment().add(1, "week").startOf("week")._d, this.moment().add(1, "week").endOf("week")._d] }
          },
          {
            text: lang.next_month,
            onClick: () => { return [this.moment().add(1, "month").startOf("month")._d, this.moment().add(1, "month").endOf("month")._d] }
          }
        ]
      } else {
        const lang = locale[this.settings.lang].shortcuts.default
        return [
          {
            text: lang.today,
            onClick: () => { return this.moment()._d }
          },
          {
            text: lang.yesterday,
            onClick: () => { return this.moment().subtract(1, "days")._d }
          },
          {
            text: lang.tomorrow,
            onClick: () => { return this.moment().add(1, "days")._d }
          },
          {
            text: lang.week_ago,
            onClick: () => { return this.moment().subtract(1, "week")._d }
          },
          {
            text: lang.week_from_now,
            onClick: () => { return this.moment().add(1, "week")._d }
          },
          {
            text: lang.month_ago,
            onClick: () => { return this.moment().subtract(1, "month")._d }
          },
          {
            text: lang.month_from_now,
            onClick: () => { return this.moment().add(1, "month")._d }
          }
        ]
      }
    },
    getLangProp () {
      const lang = this.settings.lang

      const getMonths = (type = "long") => {
        let output = []
        const months = locale[lang].format.months

        for (let i = 1; i <= 12; i++) {
          let value = months[i]
          if (type === "short") value = value.slice(0, 3)
          output.push(value)
        }

        return output
      }

      const getDays = (type = "long") => {
        let output = []
        const days = locale[lang].format.days

        for (let i = 1; i <= 7; i++) {
          let value = days[i]
          if (type === "short") value = value.slice(0, 3)
          if (type === "min") value = value.slice(0, 2)

          if (i === 7) output.unshift(value)
          else output.push(value)
        }

        return output
      }

      const firstDayOfWeek = (() => {
        const index = locale[lang].format.first_day_of_week || 1
        const days = getDays()
        const name = locale[lang].format.days[index]
        return days.indexOf(name)
      })()

      return {
        monthBeforeYear: locale[lang].format.date?.slice(0, 4) !== "YYYY",
        formatLocale: {
          months: getMonths("long"),
          monthsShort: getMonths("short"),
          weekdays: getDays("long"),
          weekdaysShort: getDays("short"),
          weekdaysMin: getDays("min"),
          firstDayOfWeek
        }
      }
    },
    props () {
      return {
        ...this.$props,
        ...this.locale,
        shortcuts: this.getShortcuts,
        ...this.settings,
        lang: this.getLangProp,
        timeTitleFormat: locale[this.settings.lang].format.date
      }
    }
  },
  created () {
    this.setSettings()
  },
  methods: {
    setSettings () {
      try {
        var config = this.$root._data.uik.datepicker || {}
      } catch {
        return
      }

      this.config = { ...this.config, ...config }
    }
  }
}
</script>

<style lang="scss">
@import 'vue2-datepicker3/scss/index.scss';

.uik-datepicker {
  .uik-datepicker__label {
    margin-top: 5px;
    line-height: 1.125rem;
    font-size: 13px;
    font-weight: 500;
    margin-bottom: 8px;
    padding-left: 0;
    color: $textSecondary;
    white-space: nowrap;
    width: 100%;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .uik-datepicker__datepicker {
    margin: 0;
    width: 100%;
    display: flex;

    .mx-input-wrapper {
      width: 100%;

      .mx-input {
        width: 100%;
        background: transparent;
        border: $borderDefault;
        box-shadow: none;
        cursor: text;
        transition: all 0.15s;
        height: unset;
        background: white;
        border-radius: $borderRadius;
        padding: 10px 14px 11px 14px;
        color: $textMain;
        font-weight: 500;
        font-size: 14px;
        line-height: 18px;
        border-color: $borderColor;
        background-color: lighten($borderColor, 3.5%);
        margin-bottom: 0;

        &:hover {
          background-color: $borderColor;
          border-color: $borderColorDarker;
        }

        &:focus {
          border-color: $primary;
          transition-duration: 0.15s;
          background: white;
          box-shadow: none;
        }

        &::placeholder {
          color: $textSecondary;
          font-weight: 400;
        }

        & ~ i:not(.mx-icon-clear) {
          display: none;
        }

        &[disabled] {
          cursor: default;
          color: $textSecondary;
          font-weight: 400;
          background-color: lighten($borderColor, 3.5%);
          opacity: 1;
          border-color: $borderColor;
        }

        &[readonly] {
          cursor: pointer;
        }
      }

      .mx-icon-clear {
        right: 2px;
        background: white;
        height: calc(100% - 4px);
        width: 35px;
        display: flex;
        justify-content: center;
        align-items: center;
        border-radius: 0 10px 10px 0;
      }

      .mx-input-append {
        display: none;
      }
    }
  }

  &--error {
    .uik-datepicker__datepicker {
      .mx-input-wrapper {
        .mx-input {
          border-color: $error !important;
          background: white !important;
        }
      }
    }
  }

  .uik-datepicker__error-message {
    line-height: 1.375rem;
    font-weight: 500;
    color: $error;
  }
}

body {
  .mx-datepicker-popup {
    border-radius: $borderRadius;
    background: white;
    border-color: darken($borderColor, 2.5%);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
    z-index: 10000;

    * {
      font-family: $fontFamily;
    }

    .mx-datepicker-body {
      position: static;
    }

    .mx-calendar-header {
      > .mx-btn {
        color: rgba($textSecondary, 0.8);

        i {
          &::before,
          &::after {
            border-width: 4px 0 0 4px;
            width: 12px;
            height: 12px;
          }
        }

        &:hover {
          color: $primary;
          transform: scale(1.1);
        }

        &:active {
          transform: none;
        }
      }

      .mx-calendar-header-label {
        .mx-btn-current-month,
        .mx-btn-current-year,
        > .mx-btn {
          color: rgba($textMain, 0.9);
          font-weight: 500;

          &:hover {
            color: $primary;
          }
        }

        > span {
          color: $textBase;
          font-weight: 500;
        }
      }
    }

    .mx-calendar-content {
      .mx-table {
        thead {
          tr {
            th {
              color: rgba($textBase, 0.9);
              font-weight: 600;
            }
          }
        }

        .cell {
          color: $textBase;
          font-weight: 500;
          border-radius: 3px;
          transition: none;

          &:hover {
            background: rgba($primary, 0.15);
            color: $primary;
          }

          &:active {
            background: rgba($primary, 0.25);
          }
        }

        .not-current-month {
          color: $textLight;
        }

        .in-range,
        .hover-in-range {
          border-radius: 0;
          background: rgba($primary, 0.15);

          &.not-current-month {
            color: rgba($textSecondary, 0.5);
          }
        }

        .today {
          &, &.not-current-month {
            color: $primary;
            font-weight: 600;
            box-shadow: inset 0 0 0 1px $primary;
          }
        }

        .in-range {
          &:hover {
            background: rgba($primary, 0.25);
          }

          &:active {
            background: rgba($primary, 0.35);
          }
        }

        .active {
          &, &:hover, &:active {
            color: white;
            font-weight: 500;
            background: $primary;
            box-shadow: none;
          }
        }
      }
    }

    .mx-calendar-week-mode {
      .mx-calendar-content {
        .mx-table {
          .cell {
            &, &:hover, &:active {
              border-radius: 0;
              background: transparent !important;
              color: $textMain;

              &.not-current-month {
                color: $textLight;
              }

              &.today {
                color: $primary;
              }

              &:nth-child(2) {
                border-radius: 3px 0 0 3px;
              }

              &:last-child {
                border-radius: 0 3px 3px 0;
              }

              &.in-range,
              &.hover-in-range {
                background: rgba($primary, 0.15) !important;

                &.not-current-month:not(.today) {
                  color: rgba($textSecondary, 0.5);
                }
              }
            }
          }

          .mx-date-row {
            background: transparent !important;

            &:hover, &:active {
              .cell {
                &.not-current-month:not(.today) {
                  color: rgba($textSecondary, 0.5);
                }

              }

              .mx-week-number {
                color: $textBase;
              }
            }

            &:hover {
              .cell {
                background: rgba($primary, 0.15) !important;
              }

              &:not(.mx-active-week) {
                .cell {
                  &.in-range {
                    background: rgba($primary, 0.25) !important;
                  }
                }
              }
            }

            &:active {
              .cell {
                background: rgba($primary, 0.25) !important;
              }

              &:not(.mx-active-week) {
                .cell {
                  &.in-range {
                    background: rgba($primary, 0.35) !important;
                  }
                }
              }
            }

            &:not(.mx-active-week) {
              &, &:hover, &:active {
                .cell {
                  border-radius: 0;
                }
              }
            }

            .mx-week-number {
              color: rgba($textBase, 0.45);
              font-weight: 600;
              opacity: 1;
              font-size: 12px;
              transform: translateX(-5px);
              padding-right: 5px;
              border-right: $borderDefault;
            }

            &.mx-active-week {
              .cell {
                &, &:hover, &:active {
                  background: $primary !important;
                  color: white !important;
                }
              }

              .mx-week-number {
                color: $primary;
              }
            }
          }
        }
      }
    }

    .mx-range-wrapper {
      width: calc(496px / 2);

      .mx-calendar:not(:nth-child(1)) {
        display: none;
      }
    }

    .mx-time {
      border-radius: $borderRadius;

      .mx-time-header {
        border-color: $borderColor;

        .mx-btn {
          color: rgba($textMain, 0.9);
          font-weight: 500;

          &:hover {
            transform: none;
            color: $primary;
          }
        }
      }

      .mx-time-content {
        border-radius: calc(#{$borderRadius} - 1px);
        overflow: hidden;

        .mx-time-columns {
          .mx-time-column {
            border-color: $borderColor;

            .mx-time-list {
              border-top: none;

              .mx-time-item {
                font-weight: 500;
                color: $textMain;

                &:hover {
                  background: rgba($primary, 0.15);
                  color: $primary;
                }

                &:active {
                  background: rgba($primary, 0.25);
                  color: $primary;
                }

                &.active {
                  &, &:hover, &:active {
                    color: $primary;
                    font-weight: 600;
                    background: transparent;
                    cursor: initial;
                  }
                }
              }
            }
          }
        }
      }
    }

    .mx-datepicker-footer {
      display: flex;
      flex-flow: row nowrap;
      border-top: none;
      padding: 3px 12px 12px 12px;

      > * {
        flex-grow: 1;
      }

      .mx-btn {
        padding: 7px 15px 8px 15px;
        color: $textMain;
        font-size: 14px;
        line-height: 16px;
        font-weight: 500;
        border-radius: $borderRadius;
        border-color: $borderColorDarker;

        &:hover {
          background: rgba($borderColor, 0.5);
        }

        &:active {
          background: rgba($borderColor, 1);
        }
      }
    }

    .mx-datepicker-sidebar {
      .mx-btn {
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        line-height: 1.2;
        padding: 5px;
        font-size: 12px;
        border-radius: 3px;

        &:hover {
          color: $primary;
          background: rgba($primary, 0.15);
        }

        &:active {
          color: $primary;
          background: rgba($primary, 0.25);
        }
      }

      & + .mx-datepicker-content {
        .mx-datepicker-body {
          .mx-calendar {
            border-left: $borderDefault;
          }
        }
      }
    }
  }
}

.mx-zoom-in-down-enter-active {
  transform: none;
  transform-origin: center top;
  transition: all 0.2s cubic-bezier(0.23, 1, 0.32, 1);
}

.mx-zoom-in-down-leave-active {
  transform-origin: center top;
  transform: scale(0.95) !important;
  transition: all 0.05s;
}

.mx-zoom-in-down-enter {
  opacity: 0;
  transform: scale(0.8);
}

.mx-zoom-in-leave-to {
  transform: scale(0.95) !important;
}
</style>
